import {React, useEffect} from "react";
import HeroHeader from "./HeroHeader";
import ContactCTA from "./ContactCTA";
import TEEimg from "../img/TEE_mockup.png";
import StandardViewsImg from "../img/MRP/TEE_standardviews.png";
import ME_LAX from "../img/MRP/ME_LAX.PNG";
import CompAnalysis from "../img/MRP/companalysis.PNG";
import TEEintro from "../video/intro0110-1189.mp4";
import AffinityMap from "../img/MRP/Affinity map.png";
import ImpactMap from "../img/MRP/impacteffort.PNG";
import ShaderDev from "../img/MRP/wireframe_shaderlookdev.PNG";
import Wireframe from "../img/MRP/wireframe_features.PNG";
import Wireframe2 from "../img/MRP/wireframe_layout 2.png";
import HeartRef from "../img/MRP/heart_reference.gif";
import MyocardialBand from "../img/MRP/myocardial_band.jpg";
import HeartExploded from "../img/MRP/TEE_exploded.gif";
import TEEdemo1 from "../img/MRP/TEE_demo1.gif";
import TEEdemo2 from "../img/MRP/TEE_demo2.gif";
import TEEdemo3 from "../img/MRP/TEE_demo3.gif";
import TEEdemo4 from "../img/MRP/TEE_demo4.gif";
import TEEmockup1 from "../img/MRP/TEE_mockup.png";

import TEEmockup2 from "../img/MRP/TEE_highlight.png";
import TEEmockup3 from "../img/MRP/TEE_highlight 2.png";
import TEEmockup4 from "../img/MRP/TEE_noOutline.png";

import IA from "../img/MRP/TEE_IA.png";

import DesignSystem1 from "../img/MRP/DesignSystemBG_1.png";
import DesignSystem2 from "../img/MRP/DesignSystemBG_2.png";

function TEEsim() {

  useEffect(() => {
    document.title = "ETCHNG | Developing a transesophageal echocardiography simulator for medical education";  
  
  }, []);    
  return (
    <>
      <HeroHeader
        hero_img={TEEimg}
        type="Web app —"
        name="Developing a transesophageal echocardiography simulator for medical education"
      />

      <div className="wrapper">
        <div className="wrapper__inner">
          <div className="work">
            <div className="work__info twoColumn">
              <div className="work__info__basic">
                <div className='work__info__basic__item'><h5>Date</h5>
                <p>May 2020 - Aug 2021</p></div>
                <div className='work__info__basic__item'><h5>Roles</h5>
                <p>Technical artist, UI/UX design & research</p></div>
                <div className='work__info__basic__item'> <h5>Tools</h5>
                <p>Figma, Unity, Blender, ZBrush</p></div>
                <div className='work__info__basic__item'> <h5>Collaborator</h5>
                <p>Michael Corrin</p></div>
                <div className='work__info__basic__item'>  <h5>Client</h5>
                <p>Dr. Annette Vegas (Toronto General Hospital)</p></div>
              </div>
            </div>

            <div className="work__info twoColumn">
              <h4>INTRODUCTION</h4>
              <p>
                Transesophageal echocardiography (TEE) is a perioperative
                imaging procedure used to visualize cardiac structure and
                function during cardiac surgery and resuscitative care. To
                obtain diagnostic information during this procedure, an
                ultrasound (US) probe on the tip of a long cord is manually
                passed down the patient’s esophagus until it sits directly
                posterior to the heart.
              </p>
            </div>
          </div>
          <div className="work">
            <video controls autoplay>
              <source src={TEEintro} loading="lazy" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
        <div className="work">
          <div className="work__info twoColumn">
            <h4>CHALLENGE</h4>
            <br />
            <p>
              TEE is difficult for novices, who must learn how to guide the US
              probe to specific positions adjacent to the heart while
              referencing a dynamic 2D image on a monitor as the main source of
              spatial feedback. Physicians must use this limited information to
              perform a difficult mental 3D reconstruction of the heart.
            </p>

            <img
              src={ME_LAX}
              loading="lazy"
              alt="Diagram of standard TEE views used for diagnostic procedures."
            ></img>
            <p>
              <i>
                Above: This is what a typical TEE image looks like from a
                mid-esophageal long-axis view.
              </i>
            </p>
          </div>
          <div className="work__info twoColumn">
            <img
              src={StandardViewsImg}
              loading="lazy"
              alt="Diagram of standard TEE views used for diagnostic procedures."
            ></img>
            <p>
              <i>
                Above: Diagram of standard TEE views to be achieved for
                diagnostic procedures. Credit: Willa Bradshaw and Dr. Gian-Marco
                Busato.
              </i>
            </p>

            <p>
              In collaboration with Michael Corrin, the primary goal of this
              project was to develop a Web-based simulator that provided a
              scalable and easily accessible solution for{" "}
              <span style={{ color: "#f55a59" }}>
                novice echocardiographers
              </span>{" "}
              in a low-stakes environment, to help reconcile technical and
              theoretical knowledge.
            </p>
          </div>
 
        </div>

        <div className="work">
            <div className="work__info">
              <h4>RESEARCH</h4>

              <img
                src={CompAnalysis}
                loading="lazy"
                alt="Diagram of TEE ultrasound"
              ></img>
            </div>
          </div>
          <div className="work">
            <div className="work__info twoColumn">
              <p>
                To help identify the specific niche this product needed to fill,
                I conducted a preliminary gap analysis to better understand the
                landscape. This uncovered gaps in four key areas:
              </p>
            </div>
          {/* </div> */}
          <div className="work__info twoColumn">
            <ol>
              <li>
                <b>Cost and overall fidelity</b>
                <p>
                  Current simulators position themselves at the extreme ends of
                  the cost spectrum. Lower-end web applications oftentimes offer
                  minimal functionality and do not facilitate spatiotemporal
                  understanding of structures, while the costs and complexity of
                  higher-end solutions make them not immediately suitable for
                  novices.
                </p>
              </li>
              <li>
                <b>Accurately animated heart model</b>
                <p>
                  There is a lack of animated 3D heart models that accurately
                  convey structural changes throughout the cardiac cycle, which
                  is key to understanding the 2D abstracted echocardiogram
                  images. Conversely, typically only high-fidelity simulators
                  include such realistic animated heart models.
                </p>
              </li>
              <li>
                <b>Lack of pathological models</b>
                <p>
                  Very few simulators include high-quality pathological hearts.
                  Similarly, the production time required to create such models
                  is extensive, particularly when starting from scratch.
                </p>
              </li>
              <li>
                <b>Supporting learning material</b>
                <p>
                  Without clear learning objectives and directives on how to
                  specifically use it to improve clinical skills, the tool
                  becomes a novelty item and is quickly abandoned. Such
                  exercises and objectives are oftentimes exclusive to
                  high-fidelity simulators.
                </p>
              </li>
            </ol>
          </div>
          </div>

          <div className="work">
              <img src={AffinityMap} loading="lazy" alt="Affinity map of interview results."></img>
            </div>

            <div className="work">

              <div className="work__info twoColumn" >

                <p>
                  We conducted additional interviews with TEE educators who had used a deprecated TEE
                  simulator that Michael had previously built to get a basis for what features would be of
                  greatest value. These were organized into an affinity map with the following groupings:
                </p>


              </div>
              <div className="work__info twoColumn" >
                <ol>
                  <li>
                    <b>UI/UX</b>
                    <p>The app's core functionality was usable and intuitive, and users appreciated the slider-based
                      controls and minimal onboarding required. </p>
                  </li>
                  <li>
                    <b>Heart model</b>
                    <p>The need for a highly complex heart model was not especially strong in the context of a simulator
                      for novice echocardiographers. However, further discussions with stakeholders indicated that a
                      high-fidelity model would still have strong educational value with other clinicians, such as
                      radiologists.</p>
                  </li>
                  <li>
                    <b>Probe interaction</b>
                    <p>Users also indicated the value of the highlighting the interaction of the probe with the heart in
                      3D space, as it helped reinforce the correlation of
                      the 2D ultrasound images.
                    </p>
                  </li>
                  <li><b>
                      Product delivery & scaling</b>
                    <p>While users confirmed that they highly valued the convenience and
                      ubiquity of a Web-based tool
                      over an exclusive mannequin simulator, the application itself was difficult to find. Notably, one
                      individual pointed out its value as a pocket reference tool in the OR, sparking
                      discussions about a potential mobile application.</p>
                  </li>
                </ol>


              </div>

            </div>

            <div className="work">

<div className="work__info narrowColumn" >
  <h4>IDEATION</h4>

  <p>
    I began generating and organizing ideas for potential functionality while getting results from
    interviews.
  </p>
  <p>
    This included potential control modalities to improve information-interaction mapping and degress of
    freedom, as well visualization tools to highlight how the probe interacted with the heart.
  </p>


</div>
<div className="work__info">
  <img src={ImpactMap} loading="lazy" alt="Impact-effort matrix."></img>


</div>



</div>


<div className="work">

<div className="work__info twoColumn">



  <img src={ShaderDev} loading="lazy"
    alt="Look development of the clipping effect."></img>
</div>
<div className="work__info twoColumn">
  <p>
    I experimented with several potential shaders for visual representations for the clipped portion
    of the heart model; as users valued being able to see how the TEE plane intersected the heart
    model, it was important clipped portion was not fully hidden, but was also not distracting.
  </p>

  <img src={Wireframe} loading="lazy"
    alt="Additional probe-heart interaction features."></img>
  <img src={Wireframe2} loading="lazy"
    alt="Additional probe-heart interaction features."></img>
</div>

</div>

<div className="work" id="heartSection">

<div className="work__info narrowColumn">
  <h4>HEART MODEL</h4>
  <br/>
  <p>

    Drawing on my background in anatomy and cadaver dissection, and my role as the technical artist, my goal was to create a real-time optimized heart model that accurately
    reflected proper contractile and pressure biomechanics, including the torsional motion of the
    ventricles during systole, and overall synchornization.
  </p>
  <p>
    In particular, technical requirements involved needing the lumenal and tissue spaces to be fully
    manifold and independent to accommodate the raycast-based mouseover
    highlight/label interactions Michael had designed. These sections needed to be further divided
    so allow for structure toggling fucntionality.
  </p>
 
</div>
<div className="work__info wideColumn">
  
  <iframe title="Heart (no fat)" frameBorder="0" allowFullScreen mozallowfullscreen="true"
    webkitallowfullscreen="true" allow="fullscreen; autoplay; VR" xr-spatial-tracking
    execution-while-out-of-viewport execution-while-not-rendered web-share width="100%" height="480"
    src="https://sketchfab.com/models/1796d9cd9fb14fe08d7e041a19565785/embed"> </iframe>

</div>

</div>

<div className="work">

              <div className="work__info wideColumn">

                <img src={HeartRef} loading="lazy"
                  alt="Additional probe-heart interaction features."></img>
                <img src={MyocardialBand} loading="lazy"
                  alt="Additional probe-heart interaction features."></img>
              </div>
              <div className="work__info narrowColumn">
                <p> I segmented a CT scan as my basis, and consulted experts and literature to fill in gaps in the data
                  when the scan was too low quality.</p>

                <p>
                 The scan data focused on the heart's external tissues and lumenal structures. Vena cavae, valves, portions of the aorta, and small vessels needed to be completely
                  remodeled due to the data set being of insufficient resolution.
                </p>

                <img src={HeartExploded} loading="lazy"
                alt="Look development of the clipping effect."></img>
              </div>

            </div>

            <div className="work">



<div className="work__info" id="narrowColumn">
  <h4>DESIGN</h4>

  <p>This was the <b>functional prototype</b> I developed and programmed in Unity to test interactions and asset compatibility. It
    demonstrates the following core functions:</p>
  <ul>
    <li>General 3D manipulation of the model (orbiting, panning, zooming)</li>
    <li>Manipulation of the probe's movement, visibility, and heart model interaction</li>
    <li>Clipping of the opaque structures</li>
    <li>Toggling of structure visibility, and highlighting functionality</li>
    <li>Heart animation playback</li>
  </ul>
  <br/>

</div>
<div className="work__info wideColumn">
  <iframe className="youtube" height="315" src="https://www.youtube.com/embed/MItbtGMA-Y0"
    title="YouTube video player" frameBorder="0"
    allow="accelerometer; autoPlay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
    allowFullScreen></iframe>              </div>               


</div>
<p><b>This functional prototype was later refined into higher fidelity designs.</b></p>
<div className="work" >

<div className="work__info twoColumn">
<img src={TEEdemo1} loading="lazy"
alt="Additional probe-heart interaction features."></img>
</div>
<div className="work__info twoColumn">
<img src={TEEdemo2} loading="lazy"
alt="Additional probe-heart interaction features."></img>
</div>


</div>
<div className="work" >
<div className="work__info twoColumn">
<img src={TEEdemo3} loading="lazy"
alt="Additional probe-heart interaction features."></img>
</div>
<div className="work__info twoColumn">
<img src={TEEdemo4} loading="lazy"
alt="Additional probe-heart interaction features."></img>
</div>


</div>

<div className="work singleColumn">

<div className="work__info">
  <img src={IA} loading="lazy" alt="Information architecture"></img>

</div>
<p>The higher fidelity mockups:
 </p>
<div className="work__info">
  <img src={TEEmockup1} loading="lazy" alt="Simulator mockup"></img>

</div>
<div className="work__info">
  <img src={TEEmockup2} loading="lazy" alt="Simulator mockup"></img>

</div>
<div className="work__info">
  <img src={TEEmockup3} loading="lazy" alt="Simulator mockup"></img>

</div>
<div className="work__info">
  <img src={TEEmockup4} loading="lazy" alt="Simulator mockup"></img>

</div>
<div className="work__info">
  <img src={DesignSystem1} loading="lazy" alt="Design system"></img>

</div>
<div className="work__info">
  <img src={DesignSystem2} loading="lazy" alt="Design system"></img>

</div>



</div>

        <ContactCTA />
      </div>
    </>
  );
}

export default TEEsim;
